import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/api/axios';
import { getEnv } from '@/lib/env';
import { useActiveContactsStore } from '@/stores/active-contacts.store';
import { useAuthStore } from '@/stores/auth.store';
import { useToolWatchStore } from '@/stores/tool-watch.store';

export function useMessagingSocket() {
  const queryClient = useQueryClient();
  const addFlash = useToolWatchStore((s) => s.addFlash);
  const addActiveContact = useActiveContactsStore((s) => s.addActive);
  const removeActiveContact = useActiveContactsStore((s) => s.removeActive);
  const isClientView = useAuthStore((s) => s.user?.role === 'client');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (isClientView) return;

    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 1000;
    const MAX_DELAY = 16000;

    function connect() {
      const token = getAccessToken();
      if (!token) return;

      const apiUrl = getEnv('VITE_API_URL');
      const apiOrigin = new URL(apiUrl);
      const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiOrigin.host}/listen-ws/${encodeURIComponent(token)}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string);

          if (message.type === 'ready') {
            ws.send(JSON.stringify({ type: 'subscribe_messaging' }));
            return;
          }

          if (message.type === 'messaging_update') {
            const updContactId = message.contactId ? String(message.contactId) : undefined;
            const updConversationId = message.conversationId
              ? String(message.conversationId)
              : undefined;
            // Target only the affected contact/conversation plus the list
            // queries. A bare ['contacts'] invalidation would refetch every
            // open multi-view card's timeline on every message anywhere.
            if (updContactId) {
              queryClient.invalidateQueries({ queryKey: ['contacts', updContactId] });
              queryClient.invalidateQueries({
                predicate: (q) =>
                  q.queryKey[0] === 'contacts' &&
                  typeof q.queryKey[1] === 'object' &&
                  q.queryKey[1] !== null,
              });
            } else {
              queryClient.invalidateQueries({ queryKey: ['contacts'] });
            }
            if (updConversationId) {
              queryClient.invalidateQueries({
                queryKey: ['messaging-conversations', updConversationId],
              });
              queryClient.invalidateQueries({
                queryKey: ['messaging-messages', updConversationId],
              });
              // Inbox lists key their filters as an object in slot 1.
              queryClient.invalidateQueries({
                predicate: (q) =>
                  q.queryKey[0] === 'messaging-conversations' &&
                  typeof q.queryKey[1] === 'object' &&
                  q.queryKey[1] !== null,
              });
            } else {
              queryClient.invalidateQueries({ queryKey: ['messaging-conversations'] });
              queryClient.invalidateQueries({ queryKey: ['messaging-messages'] });
            }
          }

          // A watched tool ran during a conversation: flash the contact row.
          // Toast + browser notifications are handled globally by useGlobalToolWatchNotifications.
          if (message.type === 'tool_watch') {
            if (message.contactId) {
              addFlash(String(message.contactId), {
                toolName: String(message.toolName ?? 'tool'),
                at: String(message.timestamp ?? new Date().toISOString()),
              });
            }
          }

          // Active-session lifecycle: keep the green-dot store in sync. The
          // WS event carries no direction, so refetch the sessions query —
          // it feeds inbound-only features like dynamic grid placement.
          if (message.type === 'contact_session_started' && message.contactId) {
            addActiveContact(String(message.contactId));
            queryClient.invalidateQueries({ queryKey: ['contacts', 'active-sessions'] });
          }
          if (message.type === 'contact_session_ended' && message.contactId) {
            removeActiveContact(String(message.contactId));
            queryClient.invalidateQueries({ queryKey: ['contacts', 'active-sessions'] });
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current), MAX_DELAY);
          reconnectAttemptsRef.current++;
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.close();
        wsRef.current = null;
      }
    };
  }, [queryClient, isClientView, addFlash, addActiveContact, removeActiveContact]);
}
